import { Sentry } from '@sonnen/web-utils';
import { setSentryInstance } from './sentryState';

/**
 * Sentry initialization for error tracking and performance monitoring.
 *
 * - Initialize early before React render
 * - Set release from VITE_SENTRY_RELEASE
 * - Set environment from VITE_ENV
 * - Skip initialization when VITE_SENTRY_DSN is absent
 */

/**
 * Initialize Sentry with configuration from environment variables.
 * Should be called early in the application lifecycle, before React renders.
 *
 * Initialization is skipped (returns null) when:
 * - VITE_SENTRY_DSN is not set
 *
 * @returns The initialized Sentry instance, or null if initialization was skipped
 */
export const initSentry = (): Sentry | null => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_ENV || import.meta.env.MODE || 'dev';
  const release = import.meta.env.VITE_SENTRY_RELEASE;
  if (!dsn) {
    return null;
  }

  try {
    // Create Sentry instance (automatically initializes Sentry SDK)
    const sentryInstance = new Sentry(environment, dsn, true, release);
    setSentryInstance(sentryInstance);

    return sentryInstance;
  } catch {
    return null;
  }
};

export { getSentryInstance, isSentryEnabled } from './sentryState';
